import { ogImageMeta } from "@/lib/og-images";
import { createFileRoute, Link } from "@tanstack/react-router";
import { TalkToJonButton } from "@/components/talk-to-jon";
import { RelatedGuides } from "@/components/related-guides";
import { SiteFooter } from "@/components/site-footer";

const TITLE = "Best AI App Builders in 2026, Honestly Compared | Vibing Co";
const DESC =
  "Lovable, Bolt, Replit, v0 and Base44 compared by people who ship with them — what each one is actually good at, and which to pick for your build.";

const TOOLS = [
  {
    n: "Lovable",
    best: "Full apps with a real backend",
    p: "The most complete of the group: database, auth, storage and a public URL from one conversation. It's our default for client work because the output is real React and TypeScript you can keep, fork, or hand to an engineer. The trade-off is that a complete app gives you more surface to get wrong — security and data modelling still need a human pass.",
  },
  {
    n: "Bolt",
    best: "Fast browser prototypes",
    p: "Very quick from prompt to something running in the browser. Great for proving an idea in an afternoon. Less comfortable once the app needs accounts, permissions and persistent data.",
  },
  {
    n: "Replit",
    best: "Anything that needs a server",
    p: "A real cloud development environment with an agent bolted on, so it happily runs backends, scripts and cron jobs. Closer to programming than the others — powerful if you're technical, heavier if you're not.",
  },
  {
    n: "v0",
    best: "Interfaces, not apps",
    p: "The best of the group at turning a description into a good-looking React interface. It is a UI generator rather than an app builder: you'll be supplying the backend yourself.",
  },
  {
    n: "Base44",
    best: "Internal tools",
    p: "Strong at the CRUD-shaped internal app — dashboards, admin panels, request forms. Opinionated, which is exactly what you want for a tool ten colleagues will use and nobody will admire.",
  },
];

export const Route = createFileRoute("/guides/best-ai-app-builders")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: "Best AI App Builders in 2026, Honestly Compared" },
      ...ogImageMeta("guides", "Share card: best AI app builders compared"),
      { property: "og:description", content: DESC },
      { property: "og:url", content: "https://vibing.inc/guides/best-ai-app-builders" },
      { property: "og:type", content: "article" },
    ],
    links: [{ rel: "canonical", href: "https://vibing.inc/guides/best-ai-app-builders" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: "Best AI app builders in 2026, honestly compared",
          description: DESC,
          author: { "@type": "Organization", name: "the Vibing Company" },
          publisher: { "@type": "Organization", name: "the Vibing Company" },
          datePublished: "2026-09-21",
          mainEntityOfPage: "https://vibing.inc/guides/best-ai-app-builders",
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: "Best AI app builders in 2026",
          itemListElement: TOOLS.map((t, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: t.n,
          })),
        }),
      },
    ],
  }),
  component: Guide,
});

function Guide() {
  return (
    <div className="min-h-dvh bg-background text-foreground">
      <main id="main" tabIndex={-1}>
        <section
          aria-labelledby="g-h"
          className="relative mx-auto max-w-[1100px] px-6 md:px-10 pt-[clamp(9rem,18vh,13rem)] pb-12"
        >
          <h1
            id="g-h"
            className="mt-8 font-display tracking-[-0.035em] leading-[1.02]"
            style={{ fontSize: "clamp(2rem, 4.4vw, 3.5rem)" }}
          >
            Best AI app builders in 2026.
          </h1>
          <p className="mt-10 max-w-[60ch] text-xl md:text-2xl leading-snug text-foreground">
            Five tools, compared by the boring criterion that actually matters: what happens after the demo works. We're not affiliated with any of them.
          </p>
        </section>

        <article className="mx-auto max-w-[1100px] px-6 md:px-10 py-12 md:py-20 space-y-16 md:space-y-24">
          {TOOLS.map((t) => (
            <section
              key={t.n}
              className="grid md:grid-cols-12 gap-8 md:gap-10 border-t border-border/60 pt-12 md:pt-16"
            >
              <div className="md:col-span-4">
                <h2
                  className="mt-4 font-display tracking-[-0.03em] leading-[1.02]"
                  style={{ fontSize: "clamp(1.75rem, 3vw, 2.5rem)" }}
                >
                  {t.n}
                </h2>
                <p className="mt-3 text-sm uppercase tracking-wider text-muted-foreground">
                  Best for: {t.best}
                </p>
              </div>
              <div className="md:col-span-8 space-y-5 text-base md:text-lg leading-relaxed text-foreground/90 max-w-[64ch]">
                <p>{t.p}</p>
              </div>
            </section>
          ))}

          <section className="grid md:grid-cols-12 gap-8 md:gap-10 border-t border-border/60 pt-12 md:pt-16">
            <div className="md:col-span-4">
              <h2
                className="mt-4 font-display tracking-[-0.03em] leading-[1.02]"
                style={{ fontSize: "clamp(1.75rem, 3vw, 2.5rem)" }}
              >
                How to choose
              </h2>
            </div>
            <div className="md:col-span-8 space-y-5 text-base md:text-lg leading-relaxed text-foreground/90 max-w-[64ch]">
              <p>
                Pick by what the thing has to survive. A pitch demo that lives for a week can be built anywhere. A product that will hold customer data, take payments and still be edited in six months wants a builder that emits real, readable code — which is why we build client work in Lovable.
              </p>
              <p>
                The tool matters less than the review. Every one of these ships insecure defaults if nobody looks. Budget an afternoon of senior eyes before anything goes live.
              </p>
            </div>
          </section>
        </article>

        <section className="relative border-t border-border/60 mx-auto max-w-[1400px] px-6 md:px-10 py-20 md:py-28 text-center">
          <h2 className="fluid-h2 font-display max-w-[24ch] mx-auto">
            Not sure which one fits your build?
          </h2>
          <p className="mt-6 text-lg text-muted-foreground max-w-[52ch] mx-auto">
            Tell us what you're making. We'll say which tool we'd use and why — and build it with you if you want.
          </p>
          <div className="mt-10 flex justify-center">
            <TalkToJonButton variant="dark" immersive />
          </div>
          <div className="mt-8">
            <Link to="/services" className="font-display text-electric underline underline-offset-4">
              → What we do
            </Link>
          </div>
        </section>

        <RelatedGuides
          current="/guides/best-ai-app-builders"
          service={{ to: "/services/mvp-development", label: "MVP development" }}
        />
      </main>
      <SiteFooter />
    </div>
  );
}
